import { useState, useEffect } from 'react';
import { Card } from '../../../../components/ui/card';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import toast from '../../../../lib/toast';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../../lib/firebase';
import { useAuth } from '../../../../contexts/AuthContext';

interface SiteSettings {
  maintenance: {
    enabled: boolean;
  };
  header: {
    showMessage: boolean;
    message: string;
  };
}

export default function MarketplaceSettings() {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<SiteSettings>({
    maintenance: {
      enabled: false
    },
    header: {
      showMessage: false,
      message: ''
    }
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'Definicoes do site', 'marketplace'));
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data() as SiteSettings);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Não foi possível carregar as configurações do marketplace.');
    }
  };

  const handleSave = async () => {
    if (!currentUser?.companyId) {
      toast.error('Você precisa estar autenticado para salvar as configurações.');
      return;
    }

    setIsLoading(true);
    try {
      await setDoc(doc(db, 'Definicoes do site', 'marketplace'), settings);
      toast.success('Configurações atualizadas com sucesso.');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Não foi possível salvar as configurações.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <h1 className="text-2xl font-bold">Definições do Marketplace</h1>
      
      <Card className="p-6">
        <div className="space-y-6">
          {/* Modo Manutenção */}
          <div className="flex items-center justify-between space-x-4">
            <div>
              <Label htmlFor="maintenance">Modo Manutenção</Label>
              <p className="text-sm text-muted-foreground">
                Ative para colocar o marketplace em modo de manutenção
              </p>
            </div>
            <Switch
              id="maintenance"
              checked={settings.maintenance.enabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({
                  ...prev,
                  maintenance: { ...prev.maintenance, enabled: checked }
                }))
              }
            />
          </div>

          {/* Mensagem no Header */}
          <div className="flex items-center justify-between space-x-4">
            <div>
              <Label htmlFor="header-message">Mensagem no Header</Label>
              <p className="text-sm text-muted-foreground">
                Ative para mostrar uma mensagem no topo do site
              </p>
            </div>
            <Switch
              id="header-message"
              checked={settings.header.showMessage}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({
                  ...prev,
                  header: { ...prev.header, showMessage: checked }
                }))
              }
            />
          </div>

          {/* Campo da Mensagem */}
          {settings.header.showMessage && (
            <div className="space-y-2">
              <Label htmlFor="message">Texto da Mensagem</Label>
              <Input
                id="message"
                value={settings.header.message}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    header: { ...prev.header, message: e.target.value }
                  }))
                }
                placeholder="Digite a mensagem que será exibida no header..."
              />
            </div>
          )}

          {/* Botão Salvar */}
          <Button 
            onClick={handleSave} 
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? 'A salvar...' : 'Salvar Alterações'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
